import { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { supabase } from '../../supabase'
import { montant } from './format'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  Btn, IconBtn, Input, Textarea, Select, Label, Field, Row, Cluster, Pill,
  DefList, DefRow, Loading, Empty, useUI,
} from './ui'

const CONTACT_TYPES = [
  { value: 'tel', label: '📞 Téléphone' },
  { value: 'email', label: '📧 Email' },
  { value: 'whatsapp', label: '💬 WhatsApp' },
  { value: 'visite', label: '🤝 Visite' },
  { value: 'autre', label: '📝 Autre' },
]
const contactTypeLabel = (v) => CONTACT_TYPES.find((x) => x.value === v)?.label || v
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
const EMPTY_NEW = { email: '', name: '', societe: '', phone: '', canton: '', code_postal: '', localite: '', adresse: '', notes: '' }

const firstLetterOf = (name) => {
  const c = (name || '').trim().toUpperCase().charAt(0)
  return /[A-Z]/.test(c) ? c : '#'
}
const initialsOf = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}
const AVATAR_PALETTE = ['#C8A84B', '#5A7A3D', '#7A5A3D', '#8B5A6B', '#3D5A7A', '#6B7A3D', '#A85A3D', '#3D8A7A']
const avatarColor = (name) =>
  AVATAR_PALETTE[(name || '').split('').reduce((a, ch) => a + ch.charCodeAt(0), 0) % AVATAR_PALETTE.length]

const fmtLong = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })

/* ── Styles ── */
const Split = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$open ? '390px 1fr' : '1fr')};
  gap: 20px;
  align-items: start;
  @media (max-width: 1080px) { grid-template-columns: 1fr; }
`
/* Sous 1080px les deux colonnes s'empilent : la fiche se retrouvait derrière
   toute la liste, hors de vue. En maître-détail, l'une chasse l'autre et le
   retour est explicite. */
const Col = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  @media (max-width: 1080px) { ${(p) => p.$masque && css`display: none;`} }
`
const SurMobile = styled.div`
  display: none;
  @media (max-width: 1080px) { display: block; }
`

const SplitBar = styled.div`
  display: flex; height: 9px; border-radius: 999px; overflow: hidden;
  background: ${t.surfaceSunk}; margin-bottom: 13px;
  div { transition: width 0.6s ease; }
`
const AlertBar = styled.button`
  width: 100%; text-align: left;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  background: ${t.amberWash}; border: 1px solid #F0DFC2;
  border-radius: ${t.rSm}; padding: 9px 13px;
  font-family: ${t.font}; font-size: 0.795rem; font-weight: 560;
  letter-spacing: -0.01em; color: ${t.amber};
  cursor: pointer; transition: border-color 0.14s;
  &:hover { border-color: ${t.amber}; }
  span:last-child { font-weight: 460; font-size: 0.73rem; opacity: 0.72; }
`
const AzBar = styled.div`
  position: sticky; top: 0; z-index: 5;
  background: rgba(255,255,255,0.95); backdrop-filter: blur(8px);
  padding: 7px 3px; margin-bottom: 9px;
  border-bottom: 1px solid ${t.line};
  display: flex; flex-wrap: wrap; gap: 1px;
`
const AzKey = styled.button`
  /* 26px de haut : au doigt sur iPhone, une lettre de 20px se rate une fois
     sur deux, et elles sont côte à côte sans marge. */
  flex: 1 0 auto; min-width: 24px; min-height: 26px; padding: 4px 0;
  border: none; border-radius: ${t.rXs};
  font-family: ${t.font}; font-size: 0.685rem; font-weight: 560;
  font-variant-numeric: tabular-nums;
  background: ${(p) => (p.$on ? t.gold : 'transparent')};
  color: ${(p) => (!p.$present ? '#D6D9D3' : p.$on ? '#fff' : t.textSoft)};
  cursor: ${(p) => (p.$present ? 'pointer' : 'default')};
  transition: background 0.14s, color 0.14s;
  &:hover { background: ${(p) => (p.$present && !p.$on ? t.surfaceSunk : undefined)}; }
`
const SearchWrap = styled.div`
  position: relative;
  input { padding-right: 34px; }
`
const SearchClear = styled.button`
  position: absolute; right: 5px; top: 50%; transform: translateY(-50%);
  width: 26px; height: 26px; border-radius: ${t.rXs};
  border: none; background: transparent; cursor: pointer;
  color: ${t.textFaint}; font-size: 0.85rem; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.14s, color 0.14s;
  &:hover { background: ${t.surfaceSunk}; color: ${t.text}; }
  &:focus-visible { outline: 2px solid ${t.gold}; outline-offset: 1px; }
`
const RosterCard = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: ${t.r}; overflow: hidden;
`
const LetterHead = styled.div`
  background: ${t.surfaceAlt};
  padding: 5px 16px;
  font-family: ${t.font}; font-size: 0.665rem; font-weight: 620;
  letter-spacing: 0.1em; color: ${t.textFaint};
  border-top: 1px solid ${t.lineSoft};
  border-bottom: 1px solid ${t.lineSoft};
  scroll-margin-top: 88px;
  &:first-child { border-top: none; }
`
const RosterRow = styled.button`
  position: relative;
  width: 100%; text-align: left;
  display: flex; align-items: center; gap: 12px;
  padding: 10px 16px;
  border: none; border-bottom: 1px solid ${t.lineSoft};
  background: ${(p) => (p.$on ? t.goldWash : t.surface)};
  cursor: pointer; transition: background 0.14s;
  font-family: ${t.font};
  &:hover { background: ${(p) => (p.$on ? t.goldWash : t.surfaceAlt)}; }
`
const Avatar = styled.span`
  width: 32px; height: 32px; border-radius: ${t.rSm}; flex-shrink: 0;
  background: ${(p) => p.$c}; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-family: ${t.font}; font-weight: 600; font-size: 0.72rem;
  letter-spacing: -0.01em;
`
const RowName = styled.div`
  display: flex; align-items: center; gap: 6px; margin-bottom: 1px;
  span:first-child {
    font-weight: 560; font-size: 0.845rem; letter-spacing: -0.016em; color: ${t.text};
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
`
const RowSub = styled.div`
  font-size: 0.8rem; color: ${t.textSoft};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const RowSide = styled.div`
  display: flex; flex-direction: column; align-items: flex-end; gap: 3px;
  flex-shrink: 0; font-size: 0.72rem; color: ${t.textFaint}; font-weight: 600;
`
const Note = styled.div`
  background: ${(p) => p.$bg || t.surfaceAlt};
  border: 1px solid ${(p) => p.$bd || t.lineSoft};
  border-radius: ${t.rSm}; padding: 11px 15px;
  font-family: ${t.font};
`
const NoteTitle = styled.div`
  font-size: 0.85rem; font-weight: 700; color: ${(p) => p.$c || t.text}; margin-bottom: 3px;
`
const NoteBody = styled.div`font-size: 0.81rem; color: ${t.textSoft}; line-height: 1.55;`

const TypeTab = styled.button`
  padding: 5px 11px; border-radius: ${t.rSm}; cursor: pointer;
  font-family: ${t.font}; font-size: 0.735rem; font-weight: 540;
  letter-spacing: -0.008em;
  transition: background 0.14s, border-color 0.14s, color 0.14s;
  background: ${(p) => (p.$on ? (p.$fid ? t.amberWash : t.greenWash) : t.surface)};
  color: ${(p) => (p.$on ? (p.$fid ? t.amber : t.green) : t.textSoft)};
  border: 1px solid ${(p) => (p.$on ? (p.$fid ? '#F0DFC2' : '#CFE6D8') : t.line)};
  &:hover { border-color: ${(p) => (p.$on ? (p.$fid ? t.amber : t.green) : '#DDDFDA')}; }
`
const HistRow = styled.div`
  background: ${t.surfaceAlt}; border-radius: ${t.rSm};
  padding: 10px 14px; margin-bottom: 6px;
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  font-family: ${t.font};
`
const HistTotal = styled.div`
  margin-top: 11px; padding-top: 11px; border-top: 2px solid ${t.line};
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  font-family: ${t.font}; font-size: 0.82rem; color: ${t.textSoft};
  b { font-family: ${t.serif}; font-size: 1.05rem; color: ${t.text}; }
`

export default function ClientsAdmin() {
  const { toast, confirm } = useUI()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [editing, setEditing] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({})
  const [newForm, setNewForm] = useState(EMPTY_NEW)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [rappelFilter, setRappelFilter] = useState(false)
  const [clientLivraisons, setClientLivraisons] = useState([])
  const [activeLetter, setActiveLetter] = useState(null)

  useEffect(() => { fetchClients() }, [])

  // Realtime pour les rappels
  useEffect(() => {
    const channel = supabase.channel('clients-rappels')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clients' }, () => fetchClients())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [])

  const fetchClients = async () => {
    setLoading(true)
    const { data: clientsData } = await supabase.from('clients').select('*').order('created_at', { ascending: false })
    const { data: ordersData } = await supabase.from('orders')
      .select('client_id, status, date_livraison, ca_total_chf, poids_total_t')
      .in('status', ['scheduled', 'delivered'])
      .not('client_id', 'is', null)
    const enriched = (clientsData || []).map((c) => {
      const clientOrders = (ordersData || []).filter((o) => o.client_id === c.id)
      const scheduled = clientOrders
        .filter((o) => o.status === 'scheduled' && o.date_livraison)
        .sort((a, b) => new Date(a.date_livraison) - new Date(b.date_livraison))[0]
      const delivered = clientOrders
        .filter((o) => o.status === 'delivered' && o.date_livraison)
        .sort((a, b) => new Date(b.date_livraison) - new Date(a.date_livraison))[0]
      return { ...c, _scheduled: scheduled || null, _delivered: delivered || null }
    })
    setClients(enriched)
    setLoading(false)
  }

  const fermerFiche = () => {
    setSelected(null); setCreating(false); setEditing(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const openClient = (c) => {
    setSelected(c)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    supabase.from('orders')
      .select('date_livraison, poids_total_t, ca_total_chf, status, lignes')
      .eq('client_id', c.id).eq('status', 'delivered')
      .not('date_livraison', 'is', null)
      .order('date_livraison', { ascending: false })
      .then(({ data }) => setClientLivraisons(data || []))
    setForm({
      name: c.name, email: c.email || '', societe: c.societe || '', phone: c.phone, canton: c.canton,
      code_postal: c.code_postal || '', localite: c.localite || '', adresse: c.adresse || '',
      notes: c.notes,
      dernier_contact_date: c.dernier_contact_date || '',
      dernier_contact_type: c.dernier_contact_type || '',
      dernier_contact_notes: c.dernier_contact_notes || '',
      rappel_date: c.rappel_date || '',
      rappel_note: c.rappel_note || '',
      rappel_fait: c.rappel_fait || false,
    })
    setEditing(false); setCreating(false)
  }

  const createClient = async () => {
    if (!newForm.email || !newForm.name) return toast('Email et nom requis', 'error')
    const { data: existing } = await supabase.from('clients').select('id').eq('email', newForm.email.toLowerCase()).single()
    if (existing) return toast('Un client avec cet email existe déjà', 'error')
    const { data, error } = await supabase.from('clients').insert([{
      email: newForm.email.toLowerCase(), name: newForm.name, societe: newForm.societe,
      phone: newForm.phone, canton: newForm.canton,
      code_postal: newForm.code_postal, localite: newForm.localite, adresse: newForm.adresse,
      notes: newForm.notes, type: 'prospect',
    }]).select().single()
    if (error) return toast('Erreur lors de la création', 'error')
    toast('Client créé')
    setCreating(false)
    setNewForm(EMPTY_NEW)
    fetchClients()
    openClient({ ...data, _scheduled: null, _delivered: null })
  }

  const saveClient = async () => {
    /* L'email identifie le client d'un bout à l'autre du suivi : on le
       normalise et on refuse le doublon plutôt que de créer deux fiches
       qui se répondent sans jamais se rejoindre. */
    const email = (form.email || '').trim().toLowerCase()
    if (!form.name?.trim() || !email) return toast('Nom et email requis', 'error')
    if (email !== selected.email) {
      const { data: existing } = await supabase.from('clients')
        .select('id').eq('email', email).neq('id', selected.id).maybeSingle()
      if (existing) return toast('Un autre client utilise déjà cet email', 'error')
    }
    const payload = {
      name: form.name, email, societe: form.societe || null, phone: form.phone, canton: form.canton,
      code_postal: form.code_postal || null, localite: form.localite || null, adresse: form.adresse || null,
      notes: form.notes,
      dernier_contact_date: form.dernier_contact_date || null,
      dernier_contact_type: form.dernier_contact_type || null,
      dernier_contact_notes: form.dernier_contact_notes || null,
      rappel_date: form.rappel_date || null,
      rappel_note: form.rappel_note || null,
      rappel_fait: form.rappel_fait || false,
      updated_at: new Date().toISOString(),
    }
    await supabase.from('clients').update(payload).eq('id', selected.id)
    toast('Fiche client mise à jour')
    setEditing(false)
    setSelected((prev) => ({ ...prev, ...payload }))
    fetchClients()
  }

  const marquerRappelFait = async () => {
    await supabase.from('clients').update({ rappel_fait: true }).eq('id', selected.id)
    setSelected((prev) => ({ ...prev, rappel_fait: true }))
    toast('Rappel marqué comme fait')
    fetchClients()
  }

  const deleteClient = async () => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer cette fiche ?',
      body: <>La fiche de <strong>{selected.name}</strong> sera effacée. Les commandes associées seront conservées.</>,
      confirmLabel: 'Supprimer la fiche',
    })
    if (!ok) return
    await supabase.from('clients').delete().eq('id', selected.id)
    toast('Client supprimé')
    setSelected(null)
    fetchClients()
  }

  const enregistrerContact = async () => {
    if (!form.dernier_contact_date) return toast('Date de contact requise', 'error')
    const payload = {
      dernier_contact_date: form.dernier_contact_date,
      dernier_contact_type: form.dernier_contact_type || 'autre',
      dernier_contact_notes: form.dernier_contact_notes || '',
      updated_at: new Date().toISOString(),
    }
    await supabase.from('clients').update(payload).eq('id', selected.id)
    setSelected((prev) => ({ ...prev, ...payload }))
    toast('Contact enregistré')
    fetchClients()
  }

  const changeType = async (type) => {
    await supabase.from('clients').update({ type }).eq('id', selected.id)
    setSelected((prev) => ({ ...prev, type }))
    fetchClients()
    toast(type === 'fidele' ? '🏆 Passé en client fidèle' : '🌱 Passé en prospect')
  }

  /* ── Dérivés ── */
  const today = new Date()
  const in3days = new Date(today); in3days.setDate(today.getDate() + 3)
  const rappelsUrgents = clients.filter((c) => c.rappel_date && !c.rappel_fait && new Date(c.rappel_date) <= in3days)

  /* On cherche sur tout ce qui identifie un client de mémoire : le nom d'abord,
     mais aussi l'exploitation, la localité ou un bout de numéro. */
  const q = search.trim().toLowerCase()
  const filtered = clients.filter((c) => {
    const matchSearch = !q || [c.name, c.societe, c.email, c.canton, c.localite, c.code_postal, c.phone]
      .some((v) => v?.toLowerCase().includes(q))
    const matchType = typeFilter === 'all' || c.type === typeFilter
    const matchRappel = !rappelFilter || (c.rappel_date && !c.rappel_fait && new Date(c.rappel_date) <= in3days)
    return matchSearch && matchType && matchRappel
  }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }))

  const lettersInList = new Set(filtered.map((c) => firstLetterOf(c.name)))
  const lettreActive = activeLetter && lettersInList.has(activeLetter) ? activeLetter : null
  const scrollToLetter = (letter) => {
    setActiveLetter(letter)
    document.getElementById(`clients-letter-${letter}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const fideles = clients.filter((c) => c.type === 'fidele').length
  const prospects = clients.filter((c) => c.type === 'prospect').length
  const pctFid = Math.round((fideles / (clients.length || 1)) * 100)

  const rappelActif = selected?.rappel_date && !selected.rappel_fait
  const rappelEnRetard = rappelActif && new Date(selected.rappel_date) < today
  const rappelProche = rappelActif && new Date(selected.rappel_date) <= in3days

  let lastLetter = null

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Clients</PageTitle>
          <PageSub>{clients.length} fiche(s) · {fideles} fidèle(s) · {prospects} prospect(s)</PageSub>
        </div>
        <Btn $variant="gold" onClick={() => { setCreating(true); setSelected(null) }}>+ Nouveau client</Btn>
      </PageHead>

      {!selected && !creating && clients.length > 0 && (
        <Stagger style={{ marginBottom: 20 }}>
          <Panel $pad="18px 22px">
            <Label>Répartition du portefeuille</Label>
            <SplitBar>
              <div style={{ width: `${pctFid}%`, background: t.amber }} />
              <div style={{ width: `${100 - pctFid}%`, background: t.green }} />
            </SplitBar>
            <Cluster style={{ justifyContent: 'space-between' }}>
              <Pill $tone="amber" $lg>🏆 {fideles} fidèle(s) · {pctFid}%</Pill>
              <Pill $tone="green" $lg>🌱 {prospects} prospect(s) · {100 - pctFid}%</Pill>
            </Cluster>
          </Panel>
        </Stagger>
      )}

      <Split $open={!!(selected || creating)}>
        {/* ══ Liste ══ */}
        <Col $masque={!!(selected || creating)}>
          {rappelsUrgents.length > 0 && (
            <AlertBar type="button" aria-pressed={rappelFilter} onClick={() => setRappelFilter((r) => !r)}>
              <span>🔔 {rappelsUrgents.length} rappel(s) à faire</span>
              <span>{rappelFilter ? 'Voir tous' : 'Filtrer'}</span>
            </AlertBar>
          )}

          <SearchWrap>
            <Input type="search" value={search} aria-label="Rechercher un client"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 Nom, exploitation, localité, téléphone…" />
            {search && (
              <SearchClear type="button" aria-label="Effacer la recherche" onClick={() => setSearch('')}>✕</SearchClear>
            )}
          </SearchWrap>

          <Cluster $gap="7px">
            {[
              { key: 'all', label: `Tous (${clients.length})`, fid: false },
              { key: 'fidele', label: `🏆 Fidèles (${fideles})`, fid: true },
              { key: 'prospect', label: `🌱 Prospects (${prospects})`, fid: false },
            ].map((f) => (
              <TypeTab key={f.key} type="button" $on={typeFilter === f.key} $fid={f.fid}
                aria-pressed={typeFilter === f.key} onClick={() => setTypeFilter(f.key)}>
                {f.label}
              </TypeTab>
            ))}
          </Cluster>

          {loading ? (
            <Loading rows={5} h="66px" />
          ) : filtered.length === 0 ? (
            <Empty icon="👥" title="Aucun client trouvé">
              {search ? (
                <>
                  Aucune fiche ne correspond à « {search} ».{' '}
                  <Btn $variant="quiet" $size="sm" onClick={() => setSearch('')}>Effacer la recherche</Btn>
                </>
              ) : 'Créez votre première fiche client.'}
            </Empty>
          ) : (
            <div>
              <AzBar>
                {ALPHABET.map((L) => {
                  const present = lettersInList.has(L)
                  return (
                    <AzKey key={L} type="button" $present={present} $on={lettreActive === L}
                      disabled={!present} aria-label={`Aller aux clients en ${L}`}
                      onClick={() => present && scrollToLetter(L)}>
                      {L}
                    </AzKey>
                  )
                })}
              </AzBar>

              <RosterCard>
                {filtered.map((c) => {
                  const letter = firstLetterOf(c.name)
                  const isFirst = letter !== lastLetter
                  lastLetter = letter
                  const urgent = c.rappel_date && !c.rappel_fait && new Date(c.rappel_date) <= in3days
                  const late = c.rappel_date && !c.rappel_fait && new Date(c.rappel_date) < today
                  return (
                    <div key={c.id}>
                      {isFirst && <LetterHead id={`clients-letter-${letter}`}>{letter}</LetterHead>}
                      <RosterRow $on={selected?.id === c.id} onClick={() => openClient(c)}>
                        <Avatar $c={avatarColor(c.name)}>{initialsOf(c.name)}</Avatar>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <RowName>
                            <span>{c.name}</span>
                            <span style={{ fontSize: '0.78rem', flexShrink: 0 }} role="img"
                              aria-label={c.type === 'fidele' ? 'Client fidèle' : 'Prospect'}>
                              {c.type === 'fidele' ? '🏆' : '🌱'}
                            </span>
                            {urgent && (
                              <span style={{ fontSize: '0.78rem', flexShrink: 0 }} role="img"
                                aria-label={late ? 'Rappel en retard' : 'Rappel à faire'}>
                                {late ? '⚠️' : '🔔'}
                              </span>
                            )}
                          </RowName>
                          <RowSub>{c.societe || c.email}</RowSub>
                        </div>
                        <RowSide>
                          {c.canton && <span>{c.canton}</span>}
                          {c._scheduled && (
                            <span style={{ color: t.blue }} title="Livraison prévue">
                              🚛 {new Date(c._scheduled.date_livraison).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            </span>
                          )}
                        </RowSide>
                        <span aria-hidden="true" style={{ color: t.textFaint, fontSize: '1.05rem' }}>›</span>
                      </RosterRow>
                    </div>
                  )
                })}
              </RosterCard>
            </div>
          )}
        </Col>

        {/* ══ Création ══ */}
        {creating && (
          <Col>
            <SurMobile>
              <Btn $variant="quiet" $size="sm" onClick={fermerFiche}>‹ Tous les clients</Btn>
            </SurMobile>
            <Stagger>
              <Panel $accent>
                <PanelHead>
                  <PanelTitle>Nouveau client</PanelTitle>
                  <Btn $variant="quiet" $size="sm" onClick={fermerFiche}>✕ Fermer</Btn>
                </PanelHead>
                <Row $cols="1fr 1fr">
                  <Field><Label>Nom *</Label><Input value={newForm.name} onChange={(e) => setNewForm((p) => ({ ...p, name: e.target.value }))} placeholder="Prénom Nom" /></Field>
                  <Field><Label>Société / exploitation</Label><Input value={newForm.societe} onChange={(e) => setNewForm((p) => ({ ...p, societe: e.target.value }))} placeholder="Ferme du Moulin, Haras…" /></Field>
                </Row>
                <Row $cols="1fr 1fr">
                  <Field><Label>Email *</Label><Input type="email" value={newForm.email} onChange={(e) => setNewForm((p) => ({ ...p, email: e.target.value }))} placeholder="contact@example.ch" /></Field>
                  <Field><Label>Téléphone</Label><Input value={newForm.phone} onChange={(e) => setNewForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+41 …" /></Field>
                </Row>
                <Row $cols="1fr 1fr 1fr">
                  <Field><Label>Canton / région</Label><Input value={newForm.canton} onChange={(e) => setNewForm((p) => ({ ...p, canton: e.target.value }))} placeholder="Vaud, Berne…" /></Field>
                  <Field><Label>Code postal</Label><Input value={newForm.code_postal} onChange={(e) => setNewForm((p) => ({ ...p, code_postal: e.target.value }))} placeholder="1000" /></Field>
                  <Field><Label>Localité</Label><Input value={newForm.localite} onChange={(e) => setNewForm((p) => ({ ...p, localite: e.target.value }))} placeholder="Lausanne" /></Field>
                </Row>
                <Field><Label>Adresse</Label><Input value={newForm.adresse} onChange={(e) => setNewForm((p) => ({ ...p, adresse: e.target.value }))} placeholder="Rue du Moulin 12" /></Field>
                <Field><Label>Notes internes</Label><Textarea value={newForm.notes} onChange={(e) => setNewForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Préférences, historique…" /></Field>
                <Btn $variant="gold" $block onClick={createClient}>✓ Créer le client</Btn>
              </Panel>
            </Stagger>
          </Col>
        )}

        {/* ══ Fiche ══ */}
        {selected && (
          <Col>
            <SurMobile>
              <Btn $variant="quiet" $size="sm" onClick={fermerFiche}>‹ Tous les clients</Btn>
            </SurMobile>
            {/* Identité */}
            <Stagger>
              <Panel>
                <PanelHead>
                  <div>
                    <PanelTitle>{selected.name}</PanelTitle>
                    <Cluster $gap="7px" style={{ marginTop: 9 }}>
                      {['prospect', 'fidele'].map((ty) => (
                        <TypeTab key={ty} $on={selected.type === ty} $fid={ty === 'fidele'} onClick={() => changeType(ty)}>
                          {ty === 'fidele' ? '🏆 Client fidèle' : '🌱 Prospect'}
                        </TypeTab>
                      ))}
                    </Cluster>
                  </div>
                  <Cluster $gap="7px">
                    <Btn $variant="ghost" $size="sm" onClick={() => setEditing(!editing)}>{editing ? 'Annuler' : '✏️ Modifier'}</Btn>
                    <IconBtn $sm onClick={deleteClient} title="Supprimer la fiche" aria-label="Supprimer la fiche">🗑</IconBtn>
                    <IconBtn $sm onClick={fermerFiche} title="Fermer la fiche" aria-label="Fermer la fiche">✕</IconBtn>
                  </Cluster>
                </PanelHead>

                {editing ? (
                  <>
                    <Row $cols="1fr 1fr">
                      <Field><Label>Nom *</Label><Input value={form.name || ''} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></Field>
                      <Field><Label>Email *</Label><Input type="email" value={form.email || ''} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="contact@example.ch" /></Field>
                    </Row>
                    <Row $cols="1fr 1fr">
                      <Field><Label>Téléphone</Label><Input type="tel" value={form.phone || ''} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} /></Field>
                      <Field><Label>Canton / région</Label><Input value={form.canton || ''} onChange={(e) => setForm((p) => ({ ...p, canton: e.target.value }))} /></Field>
                    </Row>
                    <Field><Label>Société / exploitation</Label><Input value={form.societe || ''} onChange={(e) => setForm((p) => ({ ...p, societe: e.target.value }))} placeholder="Ferme du Moulin, Haras…" /></Field>
                    <Row $cols="1fr 1fr">
                      <Field><Label>Code postal</Label><Input value={form.code_postal || ''} onChange={(e) => setForm((p) => ({ ...p, code_postal: e.target.value }))} /></Field>
                      <Field><Label>Localité</Label><Input value={form.localite || ''} onChange={(e) => setForm((p) => ({ ...p, localite: e.target.value }))} /></Field>
                    </Row>
                    <Field><Label>Adresse</Label><Input value={form.adresse || ''} onChange={(e) => setForm((p) => ({ ...p, adresse: e.target.value }))} /></Field>
                    <Field><Label>Notes internes</Label><Textarea value={form.notes || ''} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Préférences, historique, remarques…" /></Field>
                    <Btn $variant="gold" onClick={saveClient}>💾 Sauvegarder</Btn>
                  </>
                ) : (
                  <>
                    <DefList>
                      <DefRow><dt>Email</dt><dd>{selected.email}</dd></DefRow>
                      <DefRow><dt>Téléphone</dt><dd>{selected.phone || '—'}</dd></DefRow>
                      <DefRow><dt>Canton / région</dt><dd>{selected.canton || '—'}</dd></DefRow>
                      <DefRow><dt>Société</dt><dd>{selected.societe || '—'}</dd></DefRow>
                      <DefRow><dt>Adresse</dt><dd>{[selected.adresse, [selected.code_postal, selected.localite].filter(Boolean).join(' ')].filter(Boolean).join('\n') || '—'}</dd></DefRow>
                      <DefRow><dt>Client depuis</dt><dd>{new Date(selected.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</dd></DefRow>
                    </DefList>
                    {selected.notes && (
                      <Field style={{ marginTop: 14, marginBottom: 0 }}>
                        <Label>Notes internes</Label>
                        <Note><NoteBody>{selected.notes}</NoteBody></Note>
                      </Field>
                    )}
                  </>
                )}
              </Panel>
            </Stagger>

            {/* Actions rapides */}
            <Stagger $i={1}>
              <Panel $pad="16px 20px">
                <Label>Actions rapides</Label>
                <Cluster $gap="9px">
                  <Btn $variant="ghost" $size="sm" onClick={() => { window.location.href = `mailto:${selected.email}?subject=achatpaille.com` }}>📧 Email</Btn>
                  {selected.phone && <Btn $variant="ghost" $size="sm" onClick={() => { window.location.href = `tel:${selected.phone}` }}>📞 Appeler</Btn>}
                  {selected.phone && <Btn $variant="ghost" $size="sm" onClick={() => window.open(`https://wa.me/${selected.phone.replace(/\s+/g, '').replace('+', '')}`)}>💬 WhatsApp</Btn>}
                </Cluster>
              </Panel>
            </Stagger>

            {/* Dernier contact */}
            <Stagger $i={2}>
              <Panel>
                <PanelHead><PanelTitle $sm>📋 Dernier contact</PanelTitle></PanelHead>
                {selected.dernier_contact_date && !editing && (
                  <Note style={{ marginBottom: 14 }}>
                    <NoteTitle>
                      {contactTypeLabel(selected.dernier_contact_type)} · {new Date(selected.dernier_contact_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </NoteTitle>
                    {selected.dernier_contact_notes && <NoteBody>{selected.dernier_contact_notes}</NoteBody>}
                  </Note>
                )}
                <Row $cols="1fr 1fr">
                  <Field><Label>Date du contact</Label><Input type="date" value={form.dernier_contact_date || ''} onChange={(e) => setForm((p) => ({ ...p, dernier_contact_date: e.target.value }))} /></Field>
                  <Field>
                    <Label>Type de contact</Label>
                    <Select value={form.dernier_contact_type || ''} onChange={(e) => setForm((p) => ({ ...p, dernier_contact_type: e.target.value }))}>
                      <option value="">— Choisir —</option>
                      {CONTACT_TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    </Select>
                  </Field>
                </Row>
                <Field>
                  <Label>Notes sur ce contact</Label>
                  <Textarea value={form.dernier_contact_notes || ''} onChange={(e) => setForm((p) => ({ ...p, dernier_contact_notes: e.target.value }))} placeholder="Résumé de l'échange, points importants…" style={{ minHeight: 74 }} />
                </Field>
                <Btn $variant="ghost" onClick={enregistrerContact}>💾 Enregistrer ce contact</Btn>
              </Panel>
            </Stagger>

            {/* Rappel */}
            <Stagger $i={3}>
              <Panel $accent={rappelProche}>
                <PanelHead>
                  <PanelTitle $sm>🔔 Rappel programmé</PanelTitle>
                  {rappelActif && <Btn $variant="ghost" $size="sm" onClick={marquerRappelFait}>✅ Marquer fait</Btn>}
                  {selected.rappel_fait && <Pill $tone="green">✅ Effectué</Pill>}
                </PanelHead>

                {rappelActif && (
                  <Note
                    style={{ marginBottom: 14 }}
                    $bg={rappelEnRetard ? t.redWash : t.amberWash}
                    $bd={rappelEnRetard ? 'rgba(190,59,41,0.35)' : 'rgba(184,122,27,0.35)'}
                  >
                    <NoteTitle $c={rappelEnRetard ? t.red : t.amber}>
                      {rappelEnRetard ? '⚠️ En retard — ' : '🔔 '}
                      {new Date(selected.rappel_date).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' })}
                    </NoteTitle>
                    {selected.rappel_note && <NoteBody>{selected.rappel_note}</NoteBody>}
                  </Note>
                )}

                <Row $cols="1fr 1fr">
                  <Field><Label>Date du rappel</Label><Input type="date" value={form.rappel_date || ''} onChange={(e) => setForm((p) => ({ ...p, rappel_date: e.target.value, rappel_fait: false }))} /></Field>
                  <Field style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <Btn $variant="ghost" $block onClick={saveClient}>💾 Sauvegarder le rappel</Btn>
                  </Field>
                </Row>
                <Field $flush>
                  <Label>Note du rappel</Label>
                  <Input value={form.rappel_note || ''} onChange={(e) => setForm((p) => ({ ...p, rappel_note: e.target.value }))} placeholder="ex : rappeler pour commande foin printemps" />
                </Field>
              </Panel>
            </Stagger>

            {/* Livraisons */}
            {(selected._scheduled || clientLivraisons.length > 0) && (
              <Stagger $i={4}>
                <Panel>
                  <PanelHead><PanelTitle $sm>🚛 Livraisons</PanelTitle></PanelHead>

                  {selected._scheduled && (
                    <Note $bg={t.blueWash} $bd="rgba(44,107,168,0.3)" style={{ marginBottom: 14 }}>
                      <NoteTitle $c={t.blue}>🚛 Livraison planifiée</NoteTitle>
                      <NoteBody style={{ color: t.text, fontWeight: 600 }}>{fmtLong(selected._scheduled.date_livraison)}</NoteBody>
                      {selected._scheduled.poids_total_t && (
                        <NoteBody>{selected._scheduled.poids_total_t} T · {montant(selected._scheduled.ca_total_chf)}</NoteBody>
                      )}
                    </Note>
                  )}

                  {clientLivraisons.length > 0 && (
                    <>
                      <Label>Historique</Label>
                      {clientLivraisons.map((l, i) => (
                        <HistRow key={i}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '0.84rem', color: t.text, fontWeight: 600 }}>📦 {fmtLong(l.date_livraison)}</div>
                            {l.lignes?.length > 0 && (
                              <div style={{ fontSize: '0.77rem', color: t.textSoft, marginTop: 3 }}>
                                {l.lignes.map((lg, j) => (
                                  <span key={j}>{lg.produit}{lg.poids_t ? ` (${lg.poids_t} T)` : ''}{j < l.lignes.length - 1 ? ' · ' : ''}</span>
                                ))}
                              </div>
                            )}
                            {l.poids_total_t && <div style={{ fontSize: '0.72rem', color: t.textFaint, marginTop: 2 }}>{l.poids_total_t} T au total</div>}
                          </div>
                          {l.ca_total_chf && (
                            <div style={{ fontSize: '0.87rem', fontWeight: 700, color: t.text, whiteSpace: 'nowrap' }}>
                              {montant(l.ca_total_chf)}
                            </div>
                          )}
                        </HistRow>
                      ))}
                      <HistTotal>
                        <span>{clientLivraisons.length} livraison(s) · {clientLivraisons.reduce((s, l) => s + (l.poids_total_t || 0), 0).toFixed(1)} T au total</span>
                        <b>{montant(clientLivraisons.reduce((s, l) => s + (l.ca_total_chf || 0), 0))}</b>
                      </HistTotal>
                    </>
                  )}
                </Panel>
              </Stagger>
            )}
          </Col>
        )}
      </Split>
    </>
  )
}
